import {
  BarChart3,
  BookOpen,
  Boxes,
  CreditCard,
  Dices,
  Globe,
  HelpCircle,
  LayoutDashboard,
  type LucideIcon,
  Camera,
  Hammer,
  MapPin,
  Palette,
  ScrollText,
  Settings,
  Swords,
  Trophy,
  UserRound,
  Users,
} from 'lucide-react';

export interface NavItem {
  id: string;
  label: string;
  to: string;
  icon: LucideIcon;
  /** Path prefixes that count as "this item". */
  match: string[];
  /** Listed only while public sharing (the Community) is switched on. */
  needsCommunity?: boolean;
}

/** Sections that have a sub-navigation. Grimm and the public pages have none. */
export type NavSection = 'hobby' | 'play' | 'you';

/**
 * Top-level navigation: four tabs. Adding a destination later is one line here (and a route).
 */
export const PRIMARY_NAV: NavItem[] = [
  { id: 'hobby', label: 'Hobby', to: '/hobby', icon: LayoutDashboard, match: ['/hobby', '/collection', '/paint-recipes', '/journal', '/projects', '/community'] },
  { id: 'play', label: 'Play', to: '/play', icon: Swords, match: ['/play', '/battles', '/rosters', '/near-me', '/tools'] },
  { id: 'grimm', label: 'Grimm', to: '/grimm', icon: BookOpen, match: ['/grimm'] },
  { id: 'you', label: 'You', to: '/you', icon: UserRound, match: ['/you', '/help'] },
];

/**
 * Second level: the sub-sections of each tab, in display order.
 * A new Hobby entry (Projects, Community) is one line in `hobby`, plus its prefix in the Hobby
 * tab's `match` above so the tab stays highlighted.
 */
export const SECTION_NAV: Record<NavSection, NavItem[]> = {
  hobby: [
    { id: 'overview', label: 'Overview', to: '/hobby', icon: LayoutDashboard, match: ['/hobby'] },
    { id: 'collection', label: 'Collection', to: '/collection', icon: Boxes, match: ['/collection'] },
    { id: 'projects', label: 'Projects', to: '/projects', icon: Hammer, match: ['/projects'] },
    { id: 'community', label: 'Community', to: '/community', icon: Globe, match: ['/community'], needsCommunity: true },
    { id: 'journal', label: 'Journal', to: '/journal', icon: Camera, match: ['/journal'] },
    { id: 'paint-recipes', label: 'Paint recipes', to: '/paint-recipes', icon: Palette, match: ['/paint-recipes'] },
  ],
  play: [
    { id: 'battles', label: 'Battles', to: '/battles', icon: Swords, match: ['/battles'] },
    { id: 'rosters', label: 'Rosters', to: '/rosters', icon: ScrollText, match: ['/rosters'] },
    { id: 'near-me', label: 'Near me', to: '/near-me', icon: MapPin, match: ['/near-me'] },
    { id: 'tools', label: 'Tools', to: '/tools', icon: Dices, match: ['/tools'] },
  ],
  you: [
    { id: 'profile', label: 'Profile', to: '/you/profile', icon: UserRound, match: ['/you/profile'] },
    { id: 'friends', label: 'Friends', to: '/you/friends', icon: Users, match: ['/you/friends'] },
    { id: 'achievements', label: 'Achievements', to: '/you/achievements', icon: Trophy, match: ['/you/achievements'] },
    { id: 'stats', label: 'Stats', to: '/you/stats', icon: BarChart3, match: ['/you/stats'] },
    { id: 'settings', label: 'Settings', to: '/you/settings', icon: Settings, match: ['/you/settings'] },
    { id: 'plan', label: 'Plan & usage', to: '/you/plan', icon: CreditCard, match: ['/you/plan'] },
    { id: 'help', label: 'Help', to: '/help', icon: HelpCircle, match: ['/help'] },
  ],
};

export function isNavItemActive(item: NavItem, pathname: string): boolean {
  return item.match.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}
